import { once } from 'radashi'
import { useEffect } from 'react'

import {
  selectIsAppReady,
  selectJourney,
  selectJourneyPublicToken,
  selectLauncherJourney
} from '../../store/selectors/config'
import {
  selectCurrentStep,
  selectIsPreview,
  selectSubmittedAt
} from '../../store/selectors/runtime'
import { TIME_TO_READINESS } from '../../utils/config'
import { debug } from '../../utils/debug'
import { endTimer } from '../../utils/timers'
import { useAppStoreSelector } from '../useAppStoreSelector'
import { usePrevious } from '../usePrevious'

import type { EpilotPluginMethods } from './types'
import { useAnalyticsInstanceRef } from './useAnalyticsInstanceRef'

/**
 * Ensures the underlying metric is stopped only once.
 */
const endTimeToReadiness = once(() => endTimer(TIME_TO_READINESS))

/**
 * This hook is responsible for our own Product analytics tracking.
 */
export const useProductAnalytics = () => {
  const analytics = useAnalyticsInstanceRef()
  const isReady = useAppStoreSelector(selectIsAppReady)
  const isPreview = useAppStoreSelector(selectIsPreview)
  const submittedAt = useAppStoreSelector(selectSubmittedAt)
  const selectedJourney = useAppStoreSelector(selectJourney)
  const journeyToken = useAppStoreSelector(selectJourneyPublicToken)
  const currentStep = useAppStoreSelector(selectCurrentStep)
  const launcherJourney = useAppStoreSelector(selectLauncherJourney)
  const lastCurrentStep = usePrevious(currentStep)
  const enabled = isReady && !isPreview && !!selectedJourney?.id

  useEffect(() => {
    if (!enabled) return

    const analyticsRef = analytics.current
    const epilotPlugin = analyticsRef?.plugins['epilot'] as EpilotPluginMethods
    const timeToReadinessMs = endTimeToReadiness()

    if (typeof timeToReadinessMs !== 'undefined') {
      analyticsRef?.track('journey_load_time', {
        journeyId: selectedJourney.id,
        journeyName: selectedJourney.name,
        journeyLoadTime: timeToReadinessMs / 1_000 // from ms to seconds
      })
    }

    const sessionStarter = (event: Event) => {
      epilotPlugin.createSession(
        journeyToken,
        selectedJourney,
        launcherJourney,
        event
      )
    }

    const sessionEnder = () => {
      analyticsRef?.track('journey_exit', {
        journeyId: selectedJourney.id,
        journeyName: selectedJourney.name,
        lastStepNavigated: currentStep?.id // current step is the last
      })

      void analyticsRef?.reset()
    }

    /**
     * Observed events in order to start the session.
     * Currently, there are cases where Journeys get initialized without being presented to the
     * end-user and therefore shouldn't start a session until some interaction happens.
     *
     * This observes a few events to cover all cases (i.e. desktop, mobile, accessibility tools)
     */
    const starterEvents = ['click', 'mouseover', 'keydown']

    starterEvents.forEach((event) =>
      document.addEventListener(event, sessionStarter, { once: true })
    )
    document.addEventListener('beforeunload', sessionEnder)
    debug('[useProductAnalytics] added event listeners')

    return () => {
      starterEvents.forEach((event) =>
        document.removeEventListener(event, sessionStarter)
      )
      document.removeEventListener('beforeunload', sessionEnder)
      sessionEnder() // reset analytics instance to clear any previous state
      debug('[useProductAnalytics] removed event listeners')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, selectedJourney?.id])

  /**
   * Records a navigation event whenever the current Step changes,
   * indicating the user either landed on the Journey or navigated somewhere.
   *
   * Only the {@link currentStep.id} is observed and that is intentionally done to avoid
   * sending unnecessary events.
   */
  useEffect(() => {
    if (enabled && currentStep && currentStep?.id !== lastCurrentStep?.id) {
      analytics.current?.page({
        journeyId: selectedJourney.id,
        journeyName: selectedJourney.name,
        fromStepNumber: lastCurrentStep?.orderIndex,
        fromStepName: lastCurrentStep?.name,
        toStepNumber: currentStep.orderIndex,
        toStepName: currentStep.name
      })

      debug('[useProductAnalytics] recorded step navigation')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, currentStep])

  /**
   * Records a submission event
   */
  useEffect(() => {
    if (enabled && submittedAt) {
      analytics.current?.track('journey_submit', {
        journeyId: selectedJourney.id,
        journeyName: selectedJourney.name,
        submittedAt: submittedAt.toISOString()
      })

      debug('[useProductAnalytics] recorded submission')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, submittedAt])
}
